/**
 * Identity utilities for resolving effective user IDs across authentication contexts
 * 
 * This module provides two distinct helpers to avoid identity collisions:
 * - getSessionUuid: For session-scoped features (WebSocket, UI state)
 * - getOwnerUserId: For data ownership operations (permissions, notes, preferences)
 */

import type { AuthUser } from '../contexts/AuthContext';

/**
 * Gets the session UUID for the current user.
 * 
 * This should be used for session-scoped features like:
 * - WebSocket channel identity
 * - Session-specific UI state
 * - Per-session caching
 * 
 * This remains the real session actor ID, not the impersonated data owner.
 */
export function getSessionUuid(user: AuthUser | null | undefined | false): string | null {
  if (!user) return null;
  
  return user.uuid || (typeof user.id === 'string' ? user.id : null) || null;
}

export function getCharacterWorkspaceId(
  user: AuthUser | null | undefined | false,
): string | null {
  if (!user) return null;
  const sessionId = getSessionUuid(user);
  return user.impersonates_user_id
    ? `character-${user.impersonates_user_id}`
    : sessionId;
}

/**
 * Gets the owner user ID for data operations.
 * 
 * This should be used for data ownership operations like:
 * - Fetching permissions
 * - Creating/viewing notes
 * - Accessing owned data
 * 
 * For demo users, returns the impersonated owner's ID.
 * For regular users, returns their own ID.
 */
export function getOwnerUserId(user: AuthUser | null | undefined | false): string | null {
  if (!user) return null;
  
  return user.impersonates_user_id || user.uuid || (typeof user.id === 'string' ? user.id : null) || null;
}

/**
 * @deprecated Use getSessionUuid or getOwnerUserId instead based on your use case
 * 
 * Gets the effective user ID for the current user, handling both regular and demo users.
 * 
 * Priority order:
 * 1. impersonates_user_id - For demo users impersonating data owners
 * 2. uuid - The backend session stores the user ID as 'uuid'
 * 3. id - Fallback for any context that uses 'id'
 */
export function getEffectiveUserId(user: AuthUser | null | undefined | false): string | null {
  if (!user) return null;
  
  return user.impersonates_user_id || user.uuid || (typeof user.id === 'string' ? user.id : null) || null;
}
